import { useEffect, useId, useRef, useState } from 'react';
import { RecaptchaVerifier } from 'firebase/auth';
import { auth } from '../../firebaseConfig';

export function useMfaCaptcha(enabled: boolean, scope: string) {
    const id = useId();
    const verifier = useRef<RecaptchaVerifier | null>(null);
    const rejectVerification = useRef<((error: Error) => void) | null>(null);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState('');
    const [revision, setRevision] = useState(0);
    const reset = () => { setReady(false); setRevision(value => value + 1); };

    useEffect(() => {
        setReady(false);
        setError('');
        if (!enabled)
            return;
        let active = true;
        const fail = () => {
            if (!active)
                return;
            setReady(false);
            setError('The security check could not load. Retry the security check.');
            rejectVerification.current?.(new Error('The security check failed. Retry the security check.'));
        };
        let current: RecaptchaVerifier | null = null;
        const container = document.createElement('div');
        document.getElementById(id)?.appendChild(container);
        try {
            current = new RecaptchaVerifier(auth, container, {
                size: 'invisible',
                'error-callback': fail,
            });
            verifier.current = current;
            void current.render().then(() => { if (active) setReady(true); }).catch(fail);
        } catch {
            fail();
        }
        return () => {
            active = false;
            rejectVerification.current?.(new Error('Security check canceled.'));
            current?.clear();
            container.remove();
            verifier.current = null;
        };
    }, [enabled, scope, revision, id]);

    const verify = async <T,>(action: (current: RecaptchaVerifier) => Promise<T>): Promise<T> => {
        if (!ready || !verifier.current)
            throw new Error('The security check is loading. Try again shortly.');
        const failure = new Promise<never>((_, reject) => { rejectVerification.current = reject; });
        try {
            return await Promise.race([action(verifier.current), failure]);
        } finally {
            rejectVerification.current = null;
        }
    };

    return { id, verify, ready, error, reset };
}
